import React, { useState, useEffect, useCallback, useRef } from 'react';
import './NotificationBell.css';

const fetchOptions = { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/notifications', fetchOptions);
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!open) fetchNotifications();
    setOpen((prev) => !prev);
  };

  const markAsRead = async (id) => {
    try {
      const res = await fetch(`/api/notifications/${id}/read`, {
        method: 'PATCH',
        ...fetchOptions,
      });
      if (res.ok) {
        setNotifications((prev) =>
          prev.map((n) => (n._id === id ? { ...n, read: true } : n))
        );
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      const res = await fetch('/api/notifications/mark-all-read', {
        method: 'PATCH',
        ...fetchOptions,
      });
      if (res.ok) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      }
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className="notification-bell__button"
        onClick={handleToggle}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        🔔
        {unreadCount > 0 && (
          <span className="notification-bell__badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notification-bell__dropdown">
          <div className="notification-bell__header">
            <span className="notification-bell__title">Notifications</span>
            {unreadCount > 0 && (
              <button className="notification-bell__mark-all" onClick={markAllRead}>
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-bell__list">
            {loading && <p className="notification-bell__empty">Loading...</p>}
            {!loading && notifications.length === 0 && (
              <p className="notification-bell__empty">No notifications yet.</p>
            )}
            {!loading &&
              notifications.map((n) => (
                <div
                  key={n._id}
                  className={`notification-bell__item${n.read ? '' : ' notification-bell__item--unread'}`}
                  onClick={() => !n.read && markAsRead(n._id)}
                  role="button"
                  tabIndex={0}
                  aria-label={`${n.read ? 'Read' : 'Unread'}: ${n.message}`}
                  onKeyDown={(e) => e.key === 'Enter' && !n.read && markAsRead(n._id)}
                >
                  <p className="notification-bell__message">{n.message}</p>
                  <span className="notification-bell__date">{formatDate(n.createdAt)}</span>
                  {!n.read && <span className="notification-bell__unread-dot" aria-label="Unread" />}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
